import Papa from 'papaparse';
import { distinct } from './common-utils.js';

export type CSVUploadResponse<T = object> = {
  successRowsCount: number;
  createdRowsCount?: number;
  updatedRowsCount?: number;
  failedRowsCount: number;
  errors: Array<CSVUploadError>;
  warnings?: Array<CSVUploadWarning>;
} & T;

export interface CSVUploadError {
  row: number;
  message: string;
}

export type CSVUploadWarning = CSVUploadError;

export type CsvCellValue = string | number | boolean | null;

const CSV_DANGEROUS_PREFIX = /^[=\-@|%]/;

/**
 * Options to control CSV generation and escaping of dangerous values
 *
 * @property quotes - Whether to quote all fields in the CSV. Default: `true`
 * @property escapeFormulae - Escapes cell values that could be interpreted as formulas
 * (e.g. starting with `=`, `+`, `-`, or `@`). Can be `true` to use the default
 * {@link CSV_DANGEROUS_PREFIX} RegExp, or a custom RegExp for detection. Default: `CSV_DANGEROUS_PREFIX`
 * @property newline - The newline character sequence to use. Default: `'\n'`
 */
export type SafeCsvOptions = {
  quotes?: boolean;
  escapeFormulae?: boolean | RegExp;
  newline?: '\n' | '\r\n';
};

/**
 * Generates safe CSV content from rows and columns
 *
 * @param rows - Array of data objects
 * @param columns - Column definitions with IDs and value extractors
 * @param options - CSV formatting options
 * @returns The generated CSV string
 */
export function generateCSVContent<T>(
  rows: T[],
  columns: Array<{ id: string; getValue: (row: T, index: number) => CsvCellValue }>,
  options: SafeCsvOptions = {},
): string {
  if (!rows.length) {
    return '';
  }

  const header = columns.map((col) => col.id);

  const csvRows = rows.map((row, rowIndex) =>
    columns.map((col) => {
      const val = col.getValue(row, rowIndex);
      return val == null ? '' : val;
    }),
  );

  return Papa.unparse(
    { fields: header, data: csvRows },
    {
      header: true,
      quotes: options.quotes ?? true,
      escapeFormulae: options.escapeFormulae ?? CSV_DANGEROUS_PREFIX,
      newline: options.newline ?? '\n',
    },
  );
}

export type DangerCSVDetectionOptions<T extends Record<string, unknown>> = {
  fields?: (keyof T)[];
  displayNames?: Partial<Record<keyof T, string>>;
};

export interface DangerCSVDetectionResult<T> {
  field: keyof T;
  value: string;
  message: string;
}

/**
 * Detects potentially dangerous content in a CSV object.
 * @param csvObject The CSV object to check.
 * @param options Options for customizing the detection.
 * @returns A detection result if dangerous content is found, or null.
 */
export function detectDangerInCSVObject<T extends Record<string, unknown>>(
  csvObject: T,
  options: DangerCSVDetectionOptions<T> = {},
): DangerCSVDetectionResult<T> | null {
  const { fields, displayNames = {} as Partial<Record<keyof T, string>> } = options;
  const keys = fields ?? (Object.keys(csvObject) as (keyof T)[]);

  for (const key of keys) {
    const raw = csvObject[key];

    if (typeof raw !== 'string') {
      continue;
    }

    // Remove leading quotes and spaces to check the actual starting character
    const stripped = raw.replace(/^[\s'"]+/, '');

    if (CSV_DANGEROUS_PREFIX.test(stripped)) {
      const label = displayNames[key] ?? String(key);

      return {
        field: key,
        value: raw,
        message: `${label} "${raw}" starts with a potentially dangerous character (${stripped.charAt(0)}).`,
      };
    }
  }

  return null;
}

/** Split a CSV cell on commas, trim, drop empties, and collapse duplicate tokens (first occurrence wins). */
export function parseCommaSeparatedCsvTokens(rawValue: string): string[] {
  return rawValue
    .split(',')
    .map((part) => part.trim())
    .filter((token) => token.length > 0)
    .filter(distinct());
}
